/**
 * File: colorSchemeUtils.ts
 * Path: apps/admin/src/modules/layout/utils/colorSchemeUtils.ts
 * Description: Light, dark or follow-the-device colours for the admin
 * (Liam, 2026-10-08). The choice is saved on this device only, and
 * index.html applies it before the first paint so the page never flashes
 * the wrong colours; this module keeps it applied after that.
 */

import { useSyncExternalStore } from "react";

export type ColorSchemeChoice = "light" | "dark" | "system";

/** Read by the inline script in index.html too; keep the two in step. */
export const COLOR_SCHEME_KEY = "idk-admin-theme";

export const DEFAULT_COLOR_SCHEME: ColorSchemeChoice = "dark";

const LIGHT_QUERY = "(prefers-color-scheme: light)";

const isChoice = (value: unknown): value is ColorSchemeChoice =>
  value === "light" || value === "dark" || value === "system";

/** The saved choice, or Dark when nothing (or something unreadable) is saved. */
export const readColorScheme = (): ColorSchemeChoice => {
  try {
    const stored = window.localStorage.getItem(COLOR_SCHEME_KEY);
    return isChoice(stored) ? stored : DEFAULT_COLOR_SCHEME;
  } catch {
    return DEFAULT_COLOR_SCHEME;
  }
};

const deviceIsLight = (): boolean =>
  typeof window.matchMedia === "function" && window.matchMedia(LIGHT_QUERY).matches;

/** Which colours actually show for a choice on this device right now. */
export const resolveColorScheme = (choice: ColorSchemeChoice): "light" | "dark" =>
  choice === "system" ? (deviceIsLight() ? "light" : "dark") : choice;

/** The page background in each mode, for the phone's status bar (index.html too). */
export const THEME_COLORS = { light: "#f9f8f5", dark: "#100e11" } as const;

/**
 * Puts the resolved colours on <html> (data-theme drives the tokens in
 * globals.css) and tints the installed app's status bar to match.
 */
export const applyColorScheme = (choice: ColorSchemeChoice): void => {
  const root = document.documentElement;
  const resolved = resolveColorScheme(choice);
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLORS[resolved]);
};

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

export const setColorScheme = (choice: ColorSchemeChoice): void => {
  try {
    window.localStorage.setItem(COLOR_SCHEME_KEY, choice);
  } catch {
    // Private windows can refuse storage; the choice still applies until reload.
  }
  current = choice;
  applyColorScheme(choice);
  notify();
};

let current: ColorSchemeChoice | null = null;

const getSnapshot = (): ColorSchemeChoice => (current ??= readColorScheme());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Keeps "Follow my device" in step when the device switches between light
 * and dark (for example at sunset) while the admin is open.
 */
export const watchDeviceColorScheme = (): (() => void) => {
  if (typeof window.matchMedia !== "function") return () => {};
  const query = window.matchMedia(LIGHT_QUERY);
  const onChange = () => {
    if (getSnapshot() === "system") applyColorScheme("system");
  };
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
};

export const useColorScheme = (): [ColorSchemeChoice, (choice: ColorSchemeChoice) => void] => [
  useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_COLOR_SCHEME),
  setColorScheme,
];
